import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DailyReportForm from '../../components/DailyReportForm';
import { getEmployees } from '../../api/employeeApi';
import { Project } from '../../types/projectTypes';
import { Employee } from '../../types/EmployeeTypes';
import {
  createDailyReport,
  getDailyReportById,
  getDailyReportDetails,
  updateDailyReport
} from '../../api/dailyReportApi';
import { deleteDailyReportTower } from '../../api/dailyReportTowerApi';
import {
  DailyReport,
  DailyReportTower,
  DailyReportEmployee,
  DailyReportEquipment,
  DailyReportActivity
} from '../../types/dailyReportTypes';
import { getProjects } from '../../api/projectApi';
import { Typography } from '@mui/material';
import { getProjectActivities } from '../../api/activityApi';
import { buildActivityOrderMap, sortActivitiesByProjectOrder } from '../../utils/activityOrder';

interface CreateDailyReportProps {
  mode?: 'create' | 'edit';
}

const CreateDailyReport: React.FC<CreateDailyReportProps> = ({ mode }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [assignedEmployees, setAssignedEmployees] = useState(new Set<string>());
  const [assignedEquipment, setAssignedEquipment] = useState(new Set<number>());

  const [deletedTowerIds, setDeletedTowerIds] = useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();
  const [reportToEdit, setReportToEdit] = useState<DailyReport | null>(null);
  const [relatedActivities, setRelatedActivities] = useState<any[]>([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectsData = await getProjects();
        setProjects(projectsData);
        const employeesData = await getEmployees();
        setEmployees(employeesData);
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchReportToEdit = async () => {
      if (mode === 'edit' && id) {
        setLoadingReport(true);
        try {
          const dataReport = await getDailyReportById(Number(id));
          setReportToEdit(dataReport);
          // El detalle del reporte es lo crítico; el orden por actividades es secundario.
          // Si falla getProjectActivities, igual cargamos el reporte (solo se pierde el orden).
          const reportDetails = await getDailyReportDetails(Number(id));
          let projectActivities: any[] = [];
          try {
            projectActivities = await getProjectActivities(dataReport.idProject);
          } catch (e) {
            console.warn('No se pudieron cargar las actividades del proyecto (orden):', e);
          }

          const reportDetailsArray = Array.isArray(reportDetails) ? reportDetails : [reportDetails];
          const orderMap = buildActivityOrderMap(projectActivities);

          const formattedActivities = reportDetailsArray.map(detail => ({
            ...detail.activity,
            activity: detail.activity?.activity ?? detail.activity,
            workFronts: (detail.workFrontDetails || []).map((front: any) => ({
              id: front.workFront?.id,
              workFront: front.workFront,
              frontNumber: front.workFront?.frontNumber ?? front.frontNumber,
              productionRows: (front.production || []).map((p: any) => ({
                ...p, realAdvance: p.realAdvance ?? p.real_advance ?? 0
              })),
              personnelRows: front.personnel || [],
              equipmentRows: front.equipment || [],
              subcontractRows: front.subcontracts || [],
              observations: front.workFront?.observations || '',
              totalHh: front.workFront?.totalHh,
              totalHm: front.workFront?.totalHm
            })),
            _originalWrapper: detail.activity
          }));

          setRelatedActivities(sortActivitiesByProjectOrder(formattedActivities, orderMap));
        } catch (error) {
          console.error('Error al cargar el reporte:', error);
          setAlertError("No se pudo cargar el reporte solicitado.");
        } finally {
          setLoadingReport(false);
        }
      }
    };
    fetchReportToEdit();
  }, [mode, id]);

  const formatDateForBackend = (dateInput: string | Date): string => {
    if (!dateInput) return new Date().toISOString();
    const d = new Date(dateInput);
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
  };

  const handleAddDailyReport = async (
    newDailyReport: Omit<DailyReport, 'id'>,
    productionData: DailyReportTower[] | DailyReportTower[][],
    personnelData: DailyReportEmployee[] | DailyReportEmployee[][],
    equipmentData: DailyReportEquipment[] | DailyReportEquipment[][],
    addedActivities: any[],
    activityTotals: Record<number, { hh: number; hm: number; production: Record<number, number> }>,
    subcontractData?: any[]
  ) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // 1. Preparación de datos (Igual que en DailyReport.tsx)
      const totalHh = Object.values(activityTotals).reduce((sum: number, { hh }: any) => sum + (hh || 0), 0);
      const totalHm = Object.values(activityTotals).reduce((sum: number, { hm }: any) => sum + (hm || 0), 0);

      const d = new Date(newDailyReport.date);
      const pad = (num: number) => num.toString().padStart(2, '0');
      const safeDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;

      const userData = localStorage.getItem("user");
      let userObj: any = {};
      try { userObj = userData ? JSON.parse(userData) : {}; } catch (e) { console.warn("Error parseando usuario", e); }
      const safeIdentification = userObj.identificationNum || newDailyReport.identificationNum || "S/N";

      const reportHeader = {
        ...newDailyReport,
        date: safeDate,
        identificationNum: safeIdentification,
        totalHh: totalHh,
        totalHm: totalHm,
        id: 0
      };

      const flatProduction = Array.isArray(productionData) ? productionData.flat() : [];
      const flatPersonnel = Array.isArray(personnelData) ? personnelData.flat() : [];
      const flatEquipment = Array.isArray(equipmentData) ? equipmentData.flat() : [];

      const payload = {
        dailyReport: reportHeader,
        activities: addedActivities,
        productionRows: flatProduction,
        personnelRows: flatPersonnel,
        equipmentRows: flatEquipment,
        subcontractRows: Array.isArray(subcontractData) ? subcontractData.flat() : []
      };

      // 2. Llamada al Backend
      const response = await createDailyReport(payload);
      return response;

    } catch (error) {
      console.error('Error al crear el reporte diario', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDailyReport = async (
    updatedReportData: any, addedActivities: any[], activityTotals: any, initialActivities: any[]
  ) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (deletedTowerIds.length > 0) {
        await Promise.all(deletedTowerIds.map(id => deleteDailyReportTower(id)));
      }

      const totalHh = Object.values(activityTotals).reduce((sum: number, item: any) => sum + (item.hh || 0), 0);
      const totalHm = Object.values(activityTotals).reduce((sum: number, item: any) => sum + (item.hm || 0), 0);

      const safeDate = formatDateForBackend(updatedReportData.date);

      const payloadHeader = {
        ...updatedReportData,
        date: safeDate,
        totalHh: totalHh > 0 ? totalHh : (updatedReportData.totalHh || 0),
        totalHm: totalHm > 0 ? totalHm : (updatedReportData.totalHm || 0)
      };

      const productionRows = addedActivities.flatMap((a: any) => a.workFronts?.flatMap((w: any) =>
        (w.productionRows || []).map((p: any) => ({
          ...p,
          real_advance: p.realAdvance,
          natural_ground_volume_vnt: p.naturalGroundVolumeVnt,
          outcrop_volume: p.outcropVolume
        }))
      ) || []);

      const personnelRows = addedActivities.flatMap((a: any) => a.workFronts?.flatMap((w: any) => w.personnelRows) || []);
      const equipmentRows = addedActivities.flatMap((a: any) => a.workFronts?.flatMap((w: any) => w.equipmentRows) || []);
      const subcontractRows = addedActivities.flatMap((a: any) => a.workFronts?.flatMap((w: any) => w.subcontractRows || []) || []);

      const payload = {
        dailyReport: payloadHeader,
        activities: addedActivities,
        productionRows,
        personnelRows,
        equipmentRows,
        subcontractRows
      };

      const response = await updateDailyReport(updatedReportData.id!, payload);
      return response;

    } catch (error: any) {
      console.error("Error al actualizar reporte:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
      setDeletedTowerIds([]);
    }
  };

  const handleCancel = () => {
    navigate('/reporte-diario');
  };

  if (mode === 'edit' && (loadingReport || !reportToEdit)) {
    return <Typography variant="h6" sx={{ p: 4 }}>Cargando reporte para edición...</Typography>;
  }

  return (
    <DailyReportForm
      projects={projects}
      onAddDailyReport={handleAddDailyReport}
      onUpdateDailyReport={handleUpdateDailyReport}
      onCancel={handleCancel}
      assignedEmployees={assignedEmployees}
      assignedEquipment={assignedEquipment}
      onAssignEmployee={(rut) => setAssignedEmployees((prev) => new Set(prev).add(rut))}
      onAssignEquipment={(id) => setAssignedEquipment((prev) => new Set(prev).add(id))}
      unassignEmployee={(rut) => setAssignedEmployees((prev) => { const u = new Set(prev); u.delete(rut); return u; })}
      unassignEquipment={(id) => setAssignedEquipment((prev) => { const u = new Set(prev); u.delete(id); return u; })}
      submitDaily={isSubmitting}
      isEdit={mode === 'edit'}
      modifyActivities={relatedActivities}
      reportDataToEdit={reportToEdit}
      alertError={alertError}
      setAlertError={setAlertError}
      onRegisterDeletedTower={(id) => setDeletedTowerIds(prev => [...prev, id])}
    />
  );
};

export default CreateDailyReport;